import { useNavigate } from 'react-router-dom';
import { FiShield } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import BatchStatusBadge from './shared/RegistrationBatchStatusBadge';
import CreditRightBatchFilterSidebar from './shared/CreditRightBatchFilterSidebar';
import useCreditRightBatchPageList from '../../../hooks/useCreditRightBatchPageList';

const columns = [
    { id: 'id', title: 'Lote', field: 'id', canSort: true, width: '90px' },
    { id: 'operationNumber', title: 'Operação', field: 'operationNumber', canSort: false, width: '110px' },
    { id: 'contractNumber', title: 'Contrato', field: 'contractNumber', canSort: true, width: '120px' },
    { id: 'assignorName', title: 'Cedente', field: 'assignorName', canSort: false },
    { id: 'totalCount', title: 'Total', field: 'totalCount', canSort: true, cssClass: 'text-center', width: '90px' },
    { id: 'successCount', title: 'Sucessos', field: 'successCount', canSort: true, cssClass: 'text-center', width: '90px' },
    { id: 'failureCount', title: 'Falhas', field: 'failureCount', canSort: true, cssClass: 'text-center', width: '90px' },
    { id: 'createdAt', title: 'Criado em', field: 'createdAt', canSort: true, width: '150px' },
    { id: 'status', title: 'Status', field: 'status', canSort: true, cssClass: 'text-center', width: '170px' },
];

const CreditRightBatchPageList = () => {
    const navigate = useNavigate();
    const {
        rows, total, page, setPage, pageSize, changePageSize,
        isLoading, initialLoading, apiInfo, statusCounts,
        statusFilter, setStatusFilter,
        searchInput, setSearchInput, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        refresh,
    } = useCreditRightBatchPageList();

    const openBatch = (id: number | string) => navigate(`/collateral/credit-right-batches/${id}`);

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <CreditRightBatchFilterSidebar
                    statusFilter={statusFilter}
                    onStatusChange={(s: string) => { setStatusFilter(s); setPage(1); }}
                    statusCounts={statusCounts}
                />
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loading />}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={false}
                onCloseRightPanel={() => { }}
                rightPanel={null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Lotes de registro de duplicatas</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-72">
                                    <TextEdit label="Buscar contrato ou operação..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum lote encontrado</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Abrir lote ${row.id}`}
                                        className={`hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                        onClick={() => openBatch(row.id)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openBatch(row.id); } }}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-mono dark:text-white">{row.id}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">{row.operationNumber || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">{row.contractNumber || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate" title={row.assignorName}>{row.assignorName || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-white">{row.totalCount}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm text-green-700 dark:text-green-400">{row.successCount}</td>
                                        <td className={`py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm ${row.failureCount > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'dark:text-white'}`}>{row.failureCount}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">{row.createdAt || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <BatchStatusBadge status={row.status} statusFormatted={row.statusFormatted} />
                                        </td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} onItemsPerPageChange={changePageSize} itemsLabel="lotes" />
                    </div>
                </div>
            </ThreeColumnLayout>
        </>
    );
};

export default CreditRightBatchPageList;
